import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Ruler, Sparkles, Truck, Star, Quote, Instagram, Facebook } from "lucide-react";
import { useApp } from "../context/AppContext";
import api from "../lib/api";

// Rotating hero backgrounds — curated tile / interior renders
const HERO_IMAGES = [
  "https://images.unsplash.com/flagged/photo-1556438758-872c68902f60?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1694378060976-66ee61c4f427?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1640280882429-204f63d777e7?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1781249144216-143445323087?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1615873968403-89e068629265?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1618221118493-9cfa1a1c00da?auto=format&fit=crop&w=2400&q=80",
];

// Fallback quotes shown only if admin has not configured any quotes yet
const FALLBACK_QUOTES = [
  { text: "A room begins with what you walk on.", author: "TileRoom Studio" },
];

const MARBLE = "https://images.unsplash.com/photo-1694378060976-66ee61c4f427?auto=format&fit=crop&w=1400&q=80";
const RUSTIC = "https://images.unsplash.com/photo-1640280882429-204f63d777e7?auto=format&fit=crop&w=1400&q=80";
const LIFESTYLE = "https://images.unsplash.com/photo-1781249144216-143445323087?auto=format&fit=crop&w=1400&q=80";

export default function Landing() {
  const { customer } = useApp();
  const [reviews, setReviews] = useState([]);
  const [flashIdx, setFlashIdx] = useState(0);
  const [heroBgIdx, setHeroBgIdx] = useState(0);
  const [quoteIdx, setQuoteIdx] = useState(0);
  const [quotes, setQuotes] = useState(FALLBACK_QUOTES);
  const [champion, setChampion] = useState(null);

  useEffect(() => {
    api.get("/reviews", { params: { limit: 12 } }).then(r => setReviews(r.data)).catch(() => {});
    api.get("/hero-quotes").then(r => {
      if (Array.isArray(r.data) && r.data.length > 0) setQuotes(r.data);
    }).catch(() => {});
    api.get("/employee-of-the-month").then(r => { if (r.data && r.data.name) setChampion(r.data); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reviews.length === 0) return;
    const t = setInterval(() => setFlashIdx(i => (i + 1) % reviews.length), 4200);
    return () => clearInterval(t);
  }, [reviews.length]);

  // Rotate hero background every 6s
  useEffect(() => {
    const t = setInterval(() => setHeroBgIdx(i => (i + 1) % HERO_IMAGES.length), 6000);
    return () => clearInterval(t);
  }, []);

  // Rotate quote every 5s
  useEffect(() => {
    if (quotes.length <= 1) return;
    const t = setInterval(() => setQuoteIdx(i => (i + 1) % quotes.length), 5000);
    return () => clearInterval(t);
  }, [quotes.length]);

  const primaryHref = customer ? "/catalog" : "/signup";
  const primaryLabel = customer ? "Begin Shopping" : "Sign Up";
  const flash = reviews[flashIdx];
  const currentQuote = quotes[quoteIdx % quotes.length] || FALLBACK_QUOTES[0];

  return (
    <div className="bg-[#FAF9F6]" data-testid="landing-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          {HERO_IMAGES.map((src, i) => (
            <img
              key={src}
              src={src}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1800ms] ease-in-out ${i === heroBgIdx ? "opacity-100" : "opacity-0"}`}
              data-testid={`hero-bg-${i}`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/20" />
        </div>
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-24 text-white w-full">
          <p className="label-eyebrow !text-white/70 mb-6">tileroom · design your room here.</p>
          <div className="min-h-[280px] sm:min-h-[340px] lg:min-h-[400px]" data-testid="hero-quote">
            <h1
              key={quoteIdx}
              className="font-editorial text-4xl sm:text-6xl lg:text-7xl tracking-editorial leading-[1.05] font-light max-w-4xl fade-up"
            >
              &ldquo;{currentQuote.text}&rdquo;
            </h1>
            <p className="mt-6 text-[#C9A968] text-xs uppercase tracking-[0.35em]">— {currentQuote.author}</p>
          </div>
          <p className="mt-8 max-w-lg text-white/80 text-lg leading-relaxed">
            A curated house of ceramics, porcelain and stone —
            engineered for spaces that deserve to be remembered.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to={primaryHref} className="btn-clay" data-testid="landing-cta">
              {primaryLabel} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            {!customer && (
              <Link to="/login" className="btn-outline-ink !border-white !text-white hover:!bg-white hover:!text-black" data-testid="landing-signin">
                Sign In
              </Link>
            )}
          </div>

          {/* Hero bg indicator dots */}
          <div className="mt-10 flex items-center gap-2" data-testid="hero-bg-dots">
            {HERO_IMAGES.map((img, i) => (
              <button
                key={img}
                onClick={() => setHeroBgIdx(i)}
                className={`h-0.5 transition-all ${i === heroBgIdx ? "w-8 bg-[#C9A968]" : "w-4 bg-white/30"}`}
                aria-label={`Show background ${i+1}`}
              />
            ))}
          </div>

          {/* Flashing review above CTAs (one at a time) */}
          {flash && (
            <div className="mt-12 max-w-2xl" data-testid="hero-review-flash">
              <p className="label-eyebrow !text-[#C9A968] mb-4 flex items-center gap-2">
                <Quote className="w-3.5 h-3.5" strokeWidth={1.5} /> What clients are saying
              </p>
              <div key={flash.id} className="fade-up border-l-2 border-[#C9A968] pl-6 py-2">
                <div className="flex items-center gap-1 mb-2">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className={`w-3.5 h-3.5 ${i <= flash.rating ? "fill-[#C9A968] text-[#C9A968]" : "text-white/20"}`} strokeWidth={1.5} />
                  ))}
                </div>
                <p className="font-editorial text-2xl leading-snug text-white/90 italic">&ldquo;{flash.comment.slice(0, 200)}{flash.comment.length > 200 ? "…" : ""}&rdquo;</p>
                <div className="flex items-center gap-3 mt-3 text-xs">
                  <span className="text-white/70">— {flash.customer_name}</span>
                  {flash.ai_categories?.slice(0,3).map(t => (
                    <span key={t} className="text-[10px] uppercase tracking-wider text-[#C9A968]">· {t}</span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                {reviews.slice(0, Math.min(reviews.length, 12)).map((r, i) => (
                  <button
                    key={r.id || `flash-${i}`}
                    onClick={() => setFlashIdx(i)}
                    className={`h-0.5 transition-all ${i === flashIdx ? "w-8 bg-[#C9A968]" : "w-3 bg-white/30"}`}
                    aria-label={`Review ${i+1}`}
                    data-testid={`flash-dot-${i}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <p className="label-eyebrow">A Complete Experience</p>
        <h2 className="font-editorial text-4xl sm:text-5xl lg:text-6xl tracking-editorial leading-tight mt-4 max-w-3xl">
          From measurement to masterpiece — <em className="italic text-[#C9A968]">one seamless line.</em>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16 mt-20">
          {[
            { icon: Ruler, title: "Precise Calculation", body: "Enter your room dimensions. We compute area, tiles, boxes and wastage instantly." },
            { icon: Sparkles, title: "Full Photo Gallery", body: "Every tile ships with a rich multi-photo gallery — cover, close-ups, and installed shots to see the real texture before you buy." },
            { icon: Truck, title: "Tracked Dispatch", body: "Live map tracking, tile-laying service, reorder-in-one-click. Simple." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="fade-up">
              <Icon className="w-6 h-6 text-[#1E2C5A]" strokeWidth={1.5} />
              <h3 className="font-editorial text-2xl mt-6 mb-3">{title}</h3>
              <p className="text-[#5C5C5C] text-sm leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BENTO CATEGORIES */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="flex items-baseline justify-between mb-10">
          <div>
            <p className="label-eyebrow">Signature Collections</p>
            <h2 className="font-editorial text-4xl sm:text-5xl tracking-editorial mt-3">Explore the catalogue</h2>
          </div>
          <Link to="/catalog" className="link-slide text-sm hidden sm:inline" data-testid="landing-view-all">View all →</Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          <Link to="/catalog?cat=Marble" className="md:col-span-7 group img-zoom relative aspect-[16/10]" data-testid="cat-marble">
            <img src={MARBLE} alt="Marble" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-8 left-8 text-white">
              <p className="label-eyebrow !text-white/70">Collection 01</p>
              <h3 className="font-editorial text-4xl mt-2">Marble</h3>
            </div>
          </Link>
          <Link to="/catalog?cat=Rustic" className="md:col-span-5 group img-zoom relative aspect-[16/10]" data-testid="cat-rustic">
            <img src={RUSTIC} alt="Rustic" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-8 left-8 text-white">
              <p className="label-eyebrow !text-white/70">Collection 02</p>
              <h3 className="font-editorial text-4xl mt-2">Rustic</h3>
            </div>
          </Link>
          <Link to="/catalog?cat=Wood" className="md:col-span-5 group img-zoom relative aspect-[16/10]" data-testid="cat-wood">
            <img src={LIFESTYLE} alt="Wood" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-8 left-8 text-white">
              <p className="label-eyebrow !text-white/70">Collection 03</p>
              <h3 className="font-editorial text-4xl mt-2">Wood-look</h3>
            </div>
          </Link>
          <Link to="/catalog?cat=Vitrified" className="md:col-span-7 group img-zoom relative aspect-[16/10]" data-testid="cat-vitrified">
            <img src={RUSTIC} alt="Vitrified" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-8 left-8 text-white">
              <p className="label-eyebrow !text-white/70">Collection 04</p>
              <h3 className="font-editorial text-4xl mt-2">Vitrified</h3>
            </div>
          </Link>
        </div>
      </section>

      {/* REVIEWS MARQUEE */}
      {/* Sales Champion strip — Employee of the Month */}
      {champion && (
        <section className="bg-[#1E2C5A] text-white py-8 border-t border-[#C9A968]/20" data-testid="sales-champion-strip">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-center gap-6">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#C9A968] flex-shrink-0">
                {champion.photo_url ? (
                  <img src={champion.photo_url} alt={champion.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#C9A968] text-[#1E2C5A] font-editorial text-lg">
                    {(champion.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <p className="label-eyebrow !text-[#C9A968]">Employee of the Month · <Sparkles className="w-3 h-3 inline" strokeWidth={1.5} /> {champion.grade}</p>
                <p className="font-editorial text-2xl mt-1 truncate">{champion.name}</p>
                <p className="text-xs text-white/60 mt-0.5">{champion.designation} · {(champion.departments || []).join(", ")}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="label-eyebrow !text-white/50">ETS Score</p>
              <p className="font-editorial text-4xl text-[#C9A968]">{champion.ets_score}</p>
            </div>
          </div>
        </section>
      )}

      {reviews.length > 0 && (
        <section className="bg-[#F0EFEA] py-10 overflow-hidden border-t border-[#1A1A1A]/5" data-testid="reviews-marquee-section">
          <div className="flex items-center justify-between max-w-screen-2xl mx-auto px-6 md:px-12 mb-6">
            <div>
              <p className="label-eyebrow">In the client's words</p>
              <h3 className="font-editorial text-3xl mt-2">Verified reviews</h3>
            </div>
            <Link to="/reviews" className="link-slide text-sm text-[#1E2C5A]" data-testid="see-all-reviews">See all reviews →</Link>
          </div>
          <div className="marquee-wrap" data-testid="reviews-marquee">
            <div className="marquee-track">
              {[...reviews, ...reviews].map((r, i) => (
                <div key={r.id + "-" + i} className="marquee-item">
                  <div className="flex items-center gap-1 mb-2">
                    {[1,2,3,4,5].map(n => (
                      <Star key={n} className={`w-3 h-3 ${n <= r.rating ? "fill-[#C9A968] text-[#C9A968]" : "text-[#E5E5E5]"}`} strokeWidth={1.5} />
                    ))}
                  </div>
                  <p className="font-editorial text-lg leading-snug">"{r.comment.slice(0, 140)}{r.comment.length > 140 ? "…" : ""}"</p>
                  <p className="text-xs text-[#5C5C5C] mt-3">— {r.customer_name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer className="bg-[#2A363B] text-[#FAF9F6]/70 py-16">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <div className="bg-[#FAF9F6] p-3 inline-block">
              <img
                src="/tileroom-logo.png"
                alt="TileRoom — design your room here."
                className="h-24 w-auto object-contain"
              />
            </div>
            <p className="text-xs mt-4 tracking-[0.35em] uppercase text-[#C9A968]">design your room here.</p>
          </div>
          <div>
            <p className="label-eyebrow !text-white/50 mb-3">Shop</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/catalog">All Tiles</Link></li>
              <li><Link to="/tile-layers">Tile Layers</Link></li>
            </ul>
          </div>
          <div>
            <p className="label-eyebrow !text-white/50 mb-3">Contact</p>
            <ul className="space-y-2 text-sm">
              <li className="text-white">TILAKRAJ</li>
              <li>+91 95432 17216</li>
            </ul>
          </div>
          <div>
            <p className="label-eyebrow !text-white/50 mb-3">Visit</p>
            <p className="text-sm leading-relaxed">TileRoom Showroom<br/>Coimbatore, Tamil Nadu, India<br/>www.tileroom.in</p>
            <div className="flex items-center gap-3 mt-5" data-testid="footer-social">
              <a
                href="https://www.instagram.com/tileroom_cbe?igsh=MWc3bWVwNGk5OHlqcA%3D%3D&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TileRoom on Instagram"
                className="inline-flex items-center justify-center w-10 h-10 border border-white/25 hover:border-[#C9A968] hover:text-[#C9A968] transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a
                href="#"
                aria-label="TileRoom on Facebook (coming soon)"
                className="inline-flex items-center justify-center w-10 h-10 border border-white/25 hover:border-[#C9A968] hover:text-[#C9A968] transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
